import { type CacheAnalyticsCallback, promiseCache } from '../../../utils/caching/persist-refresh';
import { DegradablePromise, type DegradationCallback } from '../../utils/degradable-promise';

import { type GetAccountsByAaIDResponse, OutboundAuthApiError } from './types';

const accountsByAaIdCache = promiseCache<GetAccountsByAaIDResponse>({
	name: 'accounts-by-aaid-cache',
	expiration: 1000 * 60 * 30, // 30 minutes
});

export type AccountsByAaIdFetchProps = {
	/**
	 * Atlassian Account ID
	 */
	aaid: string;

	/**
	 * Determines if cached value should be refreshed if previous value has not expired yet
	 */
	onlyRefreshIfExpired?: boolean;

	/**
	 * Duration (in ms) that the result of this request should be cached for
	 */
	expiration?: number;

	/**
	 * Callback to execute when API is too slow and must degrade
	 */
	degradationCallback?: DegradationCallback;

	/**
	 * Callback to execute with outcome of cache operation for analytics
	 */
	cacheAnalyticsCallback?: CacheAnalyticsCallback;

	/**
	 * If true, the cached value will be returned even if it has expired
	 */
	returnExpired?: boolean;
};

/**
 * AaidAaidGet
 * Fetches all containers and their services/accounts for a given Atlassian Account ID
 * https://bitbucket.org/atlassian/outbound-auth/src/master/src/route/getAccountsForAaID.ts
 * @param aaid - Atlassian Account ID
 * @returns Promise<GetAccountsByAaIDResponse> - The API response with all containers
 */
export const fetchAccountsByAaIdInternal = async (
	aaid: string,
): Promise<GetAccountsByAaIDResponse> => {
	const url = `/gateway/api/outbound-auth/aaId/${aaid}`;

	const response = await fetch(url, {
		method: 'GET',
		headers: {
			'Content-Type': 'application/json',
		},
	});

	if (!response.ok) {
		throw new OutboundAuthApiError('Failed to fetch accounts by aaId');
	}

	return (await response.json()) as GetAccountsByAaIDResponse;
};

/**
 * Fetches all containers and their services/accounts for a given Atlassian Account ID with caching
 * @param props - Configuration object with aaid and optional callbacks
 * @returns Promise<GetAccountsByAaIDResponse> - The API response with all containers
 */
export const fetchAccountsByAaId = async ({
	aaid,
	onlyRefreshIfExpired = true,
	expiration,
	degradationCallback = () => {},
	cacheAnalyticsCallback,
	returnExpired = false,
}: AccountsByAaIdFetchProps): Promise<GetAccountsByAaIDResponse> => {
	const supplier: () => Promise<GetAccountsByAaIDResponse> = () => {
		const accountsPromise = fetchAccountsByAaIdInternal(aaid);

		const degrader = new DegradablePromise<GetAccountsByAaIDResponse>(
			accountsPromise,
			degradationCallback,
		);

		return degrader.promise();
	};

	const cacheKey = aaid;

	return accountsByAaIdCache.refreshOnAccessDecorator({
		key: cacheKey,
		supplier,
		returnExpired,
		onlyRefreshIfExpired,
		expiration,
		analyticsCallback: cacheAnalyticsCallback,
	});
};
